import { useCallback, useEffect, useState } from "react";
import { shallow } from "zustand/shallow";
import { useHeaderStore } from "../../../../../store/header.store";

import style from "./ReportIrrelevantResponseRate.module.css";
import styleForm from "../../../../../common/theme/styles/form.module.css";
import styleDataTable from "../../../../../common/UI/DataTable/dataTable.module.css";

import ProgressBar from "../../../../../common/UI/ProgressBar";
import useFormattedDate from "../../../../../common/hooks/useFormattedDate";

import ChartComponent from "../../../components/chart";
import InputSearch from "../../../../../common/UI/DataTable/inputSearch";
import { GetReportIrrelevantResponseRateService } from "../../../services/Reports/getReportIrrelevantResponseRate.service";
import { GetAnalyticsFiltersService } from "../../../../../common/services/getAnalyticsFilters.service";
import { subDays } from "date-fns";
import DataTable from "../../../../../common/UI/DataTable/dataTable";
import { GetSessionsWithPaginationService } from "../../../services/Reports/getSessionsWithPagination.service";
import AppInputSearchFilter from "../../../../../common/UI/Inputs/appInputSearchFilter";
import AppButton from "../../../../../common/UI/Buttons/AppButton"
import { GetInquiryDetailsBySessionService } from "../../../services/dashboard/getInquiryDetailsBySession.service"
import Modal from "../../../../../common/UI/Modal"
import { ConversationViewer } from "../../../components/ConversationViewer"
import { DataSelectDto } from "../../../../../common/dtos/general.dto";
import { GetWidgetsFilterWithPaginationService } from "../../../services/getWidgetsFilterWithPagination.service";
interface ChannelValues {
  channelName: string;
  values: number[];
}

interface InquiryAnalyticsResponse {
  dates: string[];
  channelValues: ChannelValues[];
}
interface ConversationMessage {
  inquiryId: string
  inquiryDate: string
  citizenId: string | null
  procedureTypeId: string | null
  question: string
  userInquiry: string
  inquiryForever: string
  inquiryDateTime: string
  inquiryGetDay: string
  inquiryStart: string
  inquiryGetMinute: string
  responseGeneratedDate: string
  responseGenerated: string
  userFeedback: string | null
}
const getSessionsWithPaginationService = new GetSessionsWithPaginationService();

export default function ReportSIrrelevantResponseRatePage() {
  const headerStore = useHeaderStore(
    (state) => ({ setModule: state.setModule, setModuleItem: state.setModuleItem }),
    shallow
  );
  const hFormatDate = useFormattedDate();
  const today = new Date();

  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<any>(subDays(today, 7));
  const [endDate, setEndDate] = useState<any>(today);
  const [responseData, setResponseData] = useState<InquiryAnalyticsResponse | null>(null);

  const [dataGraph, setDataGraph] = useState<any[]>([]);
  const [dataOptions, setDataOptions] = useState<any>({});

  // Filtros
  const [dataChannels, setDataChannels] = useState<{ label: string; value: string }[]>([]);
  const [selectChannels, setSelectChannels] = useState<{ label: string; value: number }[]>([]);

  // Estados para el modal de conversación
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([])
  const [loadingConversation, setLoadingConversation] = useState(false)
  const [widgetFilter, setWidgetFilter] = useState<DataSelectDto>({
    label: '',
    value: ''
  });
  const fetchFilters = async () => {
    try {
      const response: any = await GetAnalyticsFiltersService();
      setDataChannels(
        response.channels.map((c: any) => ({ label: c.channelName, value: c.channelId }))
      );
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };
  const [isReloadData, setIsReloadData] = useState(false);
  const serviceAllWidgets = useCallback(async (value: string) => {
    const response: any = await GetWidgetsFilterWithPaginationService({
      search: value,
      perPage: 100
    });
    return response.data.map((item: any) => ({
      label: item.name,
      value: item.widgetId,
    }));
  }, []);
  const getData = useCallback(async () => {
    setLoading(true);
    const params: any = {
      startDate: startDate ? hFormatDate(startDate, 'yyyy-MM-dd') : '',
      endDate: endDate ? hFormatDate(endDate, 'yyyy-MM-dd') : '',
      channelIds: selectChannels.map((c: any) => c.value),
      widgetId: widgetFilter ? widgetFilter.value : "",
    };
    setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación

    try {
      const response = await GetReportIrrelevantResponseRateService(params);
      setResponseData(response || null);
    } catch (error) {
      console.error('Error fetching irrelevant response rate:', error);
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  }, [endDate,widgetFilter]);

  useEffect(() => {
    if (startDate && endDate) getData();
    setIsReloadData(!isReloadData); // Recarga los datos después de la eliminación
  }, [startDate, endDate, selectChannels, widgetFilter]);
  useEffect(() => {
    headerStore.setModule('Reportes', '');
    headerStore.setModuleItem('Respuestas Irrelevantes', '/admin/home');
    fetchFilters();
  }, []);
  // Helper para leer la var CSS
  const readOnSurface = () =>
    getComputedStyle(document.documentElement)
      .getPropertyValue('--color-on-surface-variant-main')
      .trim() || '#22356a';

  const [onSurface, setOnSurface] = useState(readOnSurface());

  useEffect(() => {
    const el = document.documentElement;
    const update = () => {
      const next = readOnSurface();
      setOnSurface(prev => (prev !== next ? next : prev));
    };
    const obs = new MutationObserver(update);
    // Detecta cambios en clase/data-theme/style (incluye cambios de CSS vars)
    obs.observe(el, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    update(); // primer chequeo
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!responseData) return;

    const formattedDates = responseData.dates.map(date =>
      hFormatDate(date, "dd-MM-yyyy")
    );

    const channelData = responseData.channelValues.map(c => ({
      name: c.channelName,
      data: c.values.map(v => Number(v) || 0),
    }));

    setDataGraph(channelData);

    const numSeries = channelData.length;
    setDataOptions({
      chart: {
        type: 'area',
        height: 350,
        foreColor: onSurface, // <-- color global de textos
        zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
        toolbar: {
          show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
          autoSelected: 'zoom'
        },
      },
      theme: { palette: 'palette8' },
      stroke: { curve: 'straight', width: Array(numSeries).fill(3), dashArray: Array(numSeries).fill(0) },
      markers: { size: 5, hover: { sizeOffset: 3 } },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => val.toString(),
      },
      xaxis: {
        categories: formattedDates,
        labels: { rotate: -60, style: { fontSize: '10px', colors: onSurface } }, // <-- usa "colors"
        title: { text: 'Fechas', style: { fontSize: '12px', color: onSurface } },  // <-- usa "color"
      },
      yaxis: {
        min: 0,
        decimalsInFloat: 0,
        labels: {
          formatter: (val?: number) => (val != null ? val.toFixed(0) : '0'),
          style: { fontSize: '12px', colors: onSurface }, // <-- usa "colors"
        },
        forceNiceScale: false,
        title: { text: 'Total %', style: { fontSize: '12px', color: onSurface } },
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        labels: { colors: onSurface }, // <-- color de leyenda
        formatter: (_: string, opts: any) => channelData[opts.seriesIndex]?.name ?? 'Canal',
      },
      tooltip: { style: { fontSize: '12px', fontFamily: 'inherit' } },
      grid: { borderColor: onSurface.startsWith('#') ? `${onSurface}22` : undefined }, // línea sutil si es hex
    });
  }, [responseData, onSurface]); // <-- agrega onSurface

  const columns = [
    {
      header: "ID SESIÓN",
      field: "sessionId",
      render: (row: any) => {
        return (
          <div style={{ width: '200px' }}
          >{row.sessionId}</div>
        )
      }
    },
    {
      header: "FECHA INICIO",
      field: "",
      render: (row: any) => {
        return <>{row.startDate ? <div>{hFormatDate(row.startDate, "dd-MM-yyyy hh:mm:ss a")}</div> : ""}</>
      },
    },

    {
      header: "ESTADO",
      field: "status",
    },
    {
      header: "WIDGET ID",
      field: "widgetId",
    },
    {
      header: "CÓDIGO CIUDADANO",
      field: "",
      render: (row: any) => {
        return (
          <>
            {row.citizen ? (
              <div>{row.citizen.externalChannelId}</div>
            ) : (
              ""
            )}
          </>
        );
      },
    },
    {
      header: "CANAL",
      field: "",
      render: (row: any) => {
        return (
          <>
            {row.citizen ? (
              <div>{row.citizen.communicationChannel?.channelName}</div>
            ) : (
              ""
            )}
          </>
        );
      },
    },
    {
      header: "OPCIONES",
      field: "",
      render: (row: any) => (
        <AppButton
          className={styleDataTable.buttom_option}
          variant="primary"
          iconName="edit"
          buttonText="Ver Conversación"
          onClick={() => handleView(row)}
        />
      ),
    },
  ]


  const handleView = async (row: any) => {
    // Validate that row has the required data
    if (!row || !row.sessionId) {
      console.error("Invalid session data:", row)
      return
    }

    setIsOpen(true)
    setLoadingConversation(true)
    setConversationMessages([])

    try {
      const params = {
        sessionId: row.sessionId,
      }
      const response: any = await GetInquiryDetailsBySessionService(params)
      // Handle the specific response structure from the API
      setConversationMessages(response)
    } catch (error) {
      console.error("Error fetching conversation details:", error)
      setConversationMessages([])
    } finally {
      setLoadingConversation(false)
    }
  }




  const handleCloseModal = () => {
    setIsOpen(false)
    setConversationMessages([])
    setLoadingConversation(false)
  }
  return (
    <div className={style.Dashboard_page}>
      <h4 className={style.under__line}><strong>Tasa Respuestas Irrelevantes:</strong></h4>

      {loading && <ProgressBar />}
      <div className={style.containerGraph} style={{ marginTop: '0.5rem' }}>

        <div className={style.containerFilters}>
          <div>
            <AppInputSearchFilter
              name="channels"
              textLabel="Selecciona canal"
              data={dataChannels}
              selected={selectChannels}
              onSelect={setSelectChannels}
              multiple={true}

            />
          </div>
          <div
            className={style.container__select}
            style={{ minWidth: "280px" }}
          >
            <InputSearch
              key={crypto.randomUUID()}
              name="status"
              classNameInput={styleForm.input__input}
              textLabel="Selecciona widget"
              serviceCall={serviceAllWidgets}
              onSelect={(selected) => {
                setWidgetFilter(selected);
              }}
              selected={widgetFilter}
            />
          </div>
        </div>

        <ChartComponent
          key={`irrelevant-${onSurface}`}  // remonta cuando cambie el color del tema
          titleChart="Respuestas irrelevantes"
          typeGraph="bar"
          dataDashboard={dataGraph}
          dataOptions={dataOptions}
          onDateChange={(range) => {
            if (range) { setStartDate(range[0]); setEndDate(range[1]); }
          }}
        />

        <DataTable
          title="Listado de sesiones"
          columns={columns}
          service={getSessionsWithPaginationService}
          reloadData={isReloadData}
          // addButton={addButton}
          showSearch={false}
          params={{
            startDate: startDate ? hFormatDate(startDate, "yyyy-MM-dd") : "",
            endDate: endDate ? hFormatDate(endDate, "yyyy-MM-dd") : "",
            channelIds: selectChannels.map((c: any) => c.value),
            widgetId: widgetFilter ? widgetFilter.value : "",
          }}
          excelButton={{
            enabled: true,
            nameFile: `Lista de Sesiones`,
            getExcelData: (data) => {
              return data.map((row: any) => ({
                ID_SESIÓN: row.sessionId ?? "",
                FECHA_INICIO: row.startDate
                  ? hFormatDate(row.startDate, "dd-MM-yyyy hh:mm:ss a") // o new Date(row.startDate) si quieres celda de fecha real
                  : "",
                ESTADO: row.status ?? "",
                WIDGET_ID: row.widgetId ?? "",
                CÓDIGO_CIUDADANO: row.citizen?.externalChannelId ?? "",
                CANAL: row.citizen?.communicationChannel?.channelName ?? "",
              }));
            },
          }}
        >
          {""}
        </DataTable>

        <Modal
          title="Detalle de Conversación"
          isOpen={isOpen}
          onClose={handleCloseModal}
          size={{ width: "90%", maxWidth: "1400px", height: "80vh" }}
          loadingModal={false}
          footerContent={
            <div className={style.containerButtoms}>
              <button className={styleForm.buttomFooter + " " + styleForm.buttom_cancelar} onClick={handleCloseModal}>
                Cerrar
              </button>
            </div>
          }
        >
          {/* {selectedSessionData && ( */}
          <ConversationViewer
            messages={conversationMessages}
            loading={loadingConversation}
          />
          {/* )} */}
        </Modal>
      </div>
    </div>
  )
}